import apiClient from './client.js'

/**
 * POI管理API
 *
 * TASK-FE-105: POI管理画面実装
 *
 * エンドポイント（管理画面用 - JWT認証）:
 * - POST /console/v1/poi/register - POI登録
 * - GET /console/v1/poi/search - POI検索
 * - GET /console/v1/poi/:poiId - POI詳細取得
 * - POST /console/v1/poi/remove/:poiId - POI削除
 * - POST /console/v1/poi/move/:poiId - POI移動
 */

export const poiApi = {
  /**
   * POI登録
   * @param {Object} data - {
   *   dataset_id: string,
   *   name: string,
   *   latitude: number,
   *   longitude: number,
   *   address?: string,
   *   phone?: string,
   *   url?: string,
   *   category?: string,
   *   description?: string,
   *   image_url?: string,
   *   business_hours?: string,
   *   tags?: string[]
   * }
   * @returns {Promise<Object>} {
   *   success: boolean,
   *   data: {
   *     poi: {
   *       poi_id: string,
   *       dataset_id: string,
   *       name: string,
   *       latitude: number,
   *       longitude: number,
   *       ...
   *     }
   *   }
   * }
   */
  async create(data) {
    const response = await apiClient.post('/console/v1/poi/register', data)
    return response.data
  },

  /**
   * POI検索
   * @param {Object} params - {
   *   dataset_id?: string,
   *   keyword?: string,
   *   latitude?: number,
   *   longitude?: number,
   *   radius?: number,
   *   limit?: number,
   *   offset?: number
   * }
   * @returns {Promise<Object>} {
   *   success: boolean,
   *   data: {
   *     pois: Array<POI>,
   *     total: number,
   *     limit: number,
   *     offset: number
   *   }
   * }
   */
  async search(params = {}) {
    const response = await apiClient.get('/console/v1/poi/search', { params })
    return response.data
  },

  /**
   * POI詳細取得
   * @param {string} datasetId - データセットID
   * @param {string} poiId - POI ID
   * @returns {Promise<Object>} {
   *   success: boolean,
   *   data: {
   *     poi: {
   *       _id: string,
   *       poi_id: string,
   *       dataset_id: string,
   *       name: string,
   *       latitude: number,
   *       longitude: number,
   *       ...
   *     }
   *   }
   * }
   */
  async get(datasetId, poiId) {
    const response = await apiClient.get(`/console/v1/poi/${poiId}`, {
      params: {
        dataset_id: datasetId,
        poi_id: poiId
      }
    })
    return response.data
  },

  /**
   * POI更新（registerエンドポイントに統合）
   * @param {string} datasetId - データセットID
   * @param {string} poiId - POI ID
   * @param {Object} data - POIデータ
   * @returns {Promise<Object>} {
   *   success: boolean,
   *   data: {
   *     poi: {
   *       _id: string,
   *       name: string,
   *       latitude: number,
   *       longitude: number,
   *       ...
   *     }
   *   }
   * }
   */
  async update(datasetId, poiId, data) {
    // poi_idをリクエストボディに含める
    const requestData = {
      ...data,
      dataset_id: datasetId,
      poi_id: poiId
    }
    const response = await apiClient.post('/console/v1/poi/register', requestData)
    return response.data
  },

  /**
   * POI削除
   * @param {string} poiId - POI ID
   * @returns {Promise<Object>} {
   *   success: boolean,
   *   data: {
   *     message: string
   *   }
   * }
   */
  async delete(poiId) {
    const response = await apiClient.post(`/console/v1/poi/remove/${poiId}`)
    return response.data
  },

  /**
   * POI移動（別データセットへ）
   * @param {string} poiId - POI ID
   * @param {Object} data - { target_dataset_id: string }
   * @returns {Promise<Object>} {
   *   success: boolean,
   *   data: {
   *     message: string,
   *     target_dataset_id: string
   *   }
   * }
   */
  async move(poiId, data) {
    const response = await apiClient.post(`/console/v1/poi/move/${poiId}`, data)
    return response.data
  }
}

export default poiApi
